import React, {ReactNode, ChangeEvent, useState} from 'react'
import {css} from '@emotion/core'
import styled from '@emotion/styled'
import {isEmpty} from 'ramda'
import {
  MdKeyboardArrowDown as ChevronDown,
  MdKeyboardArrowUp as ChevronUp
} from 'react-icons/md'

import {
  AnimatedPlaceholder,
  baseLayout,
  formatLabel,
  IconWrapper,
  IconWrapperPrepended,
  IconsWrapper,
  IconsWrapperPrepended,
  InputContainer,
  InputWrapper,
  invalidState,
  getClassName,
  getIsPlaceholderAnimated,
  getShowValidIcon,
  getShowErrorMessage,
  ValidationMessageElement,
  ValidIcon,
  withAnimatedPlaceholder,
  withIconPrependedStyles,
  withBorder,
  withIcon,
  withLabel,
  withSize
} from '../InputBase'

type PropsType = {
  /** Field autocomplete id */
  autoComplete?: string
  /** The options of the select element */
  children: ReactNode
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** Whether the input is disabled */
  disabled?: boolean
  /** Message to display if `isTouched` and `hasError` is true */
  errorMessage?: string
  /** Whether the input has an error */
  hasError?: boolean
  /** An Icon to display at the end of the input */
  Icon?: ReactNode
  /** ID of this component, passed down by the controlling parent component */
  id?: string
  /** Whether this is an optional field */
  isOptional?: boolean
  /** Whether the input content is private and should be masked in FullStory */
  isPrivate?: boolean
  /** Whether this is a required field */
  isRequired?: boolean
  /** Whether the user has interacted with this input */
  isTouched?: boolean
  /** Name of this component, passed down by the controlling parent component */
  name?: string
  /** An onBlur callback passed down by the controlling parent component */
  onBlur?: (event: FocusEvent) => void
  /** An onChange callback passed down by the controlling parent component */
  onChange: (value: string) => void
  /** Callback that fires when the input is clicked */
  onClick?: () => void
  /** Content to be used as the input placeholder */
  placeholder?: string | ReactNode
  /** label of the select, if it's not passed, no label will be shown. If both placeholder and label prop has value, only label will be visible **/
  label?: string | ReactNode
  /** Formik function for setting a specific field's value. Used to update the value without sending an HTML event. */
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void
  /** The size of the input */
  size?: 'md' | 'lg'
  /** CSS text-transform style to apply to the input field */
  textTransformType?: 'uppercase' | 'capitalize' | 'lowercase' | 'none'
  /** The value passed down by the controlling parent component */
  value: string
  /** Whether the input's placeholder should be animated. Only works when `size` is `lg`. */
  withAnimatedPlaceholder?: boolean
  /** Whether the input should have a border */
  withBorder?: boolean
  /** Whether the input should show the validation tick icon. */
  withValidationIcon?: boolean
  /** Whether the input should show an up and down chevron. */
  withChevron?: boolean
  /** Whether the icon should be prepended */
  withIconPrepended?: boolean
}

const appearanceNone = css`
  appearance: none;
`

const InputSelectElement = styled('select')(
  appearanceNone,
  baseLayout,
  withLabel,
  withSize,
  withBorder,
  withIcon,
  withAnimatedPlaceholder,
  withIconPrependedStyles,
  invalidState
)

const LabelWrapper = styled('label')(
  ({theme}) => css`
    ${theme.typography.text.subtitle2};
    color: ${theme.colors.supportTextColor};
    display: inline-block;
    margin-bottom: ${theme.layout.spacing.s300}px;
  `
)

const ICON_SIZE = 24

const withDisabled = ({theme, disabled}) => css`
  color: ${disabled
    ? theme.colors.midnight.m300
    : theme.colors.supportTextColor};
`

const StyledChevronUp = styled(ChevronUp)(withDisabled)
const StyledChevronDown = styled(ChevronDown)(withDisabled)

const Chevron = ({
  direction = 'up',
  disabled
}: {
  direction: string
  disabled: boolean
}) => {
  return direction === 'up' ? (
    <StyledChevronUp size={ICON_SIZE} disabled={disabled} />
  ) : (
    <StyledChevronDown size={ICON_SIZE} disabled={disabled} />
  )
}

const InputSelect = ({
  autoComplete,
  children,
  className,
  dataId,
  disabled,
  errorMessage,
  hasError,
  Icon,
  id,
  isOptional,
  isPrivate,
  isRequired,
  isTouched,
  name,
  onBlur,
  onChange,
  onClick,
  placeholder,
  label,
  setFieldValue,
  size,
  textTransformType,
  value,
  withAnimatedPlaceholder,
  withBorder,
  withValidationIcon,
  withChevron,
  withIconPrepended
}: PropsType) => {
  const [chevronDirection, setChevronDirection] = useState('down')

  const isPlaceholderAnimated = getIsPlaceholderAnimated(
    withAnimatedPlaceholder,
    size
  )
  const showValidIcon = getShowValidIcon(
    withValidationIcon,
    isTouched,
    hasError
  )
  const showErrorMessage = getShowErrorMessage(
    isTouched,
    hasError,
    errorMessage
  )

  const handleBlur = event => {
    handleSetChevron('down')
    onBlur(event)
  }

  const combinedClassName = getClassName(isPrivate, className)
  const placeholderMessage =
    placeholder && formatLabel(placeholder, isRequired, isOptional)
  const labelMessage = label && formatLabel(label, isRequired, isOptional)

  const handleSetChevron = (direction: string) => {
    if (withChevron) {
      setChevronDirection(direction)
    }
  }

  const handleChange = (event: ChangeEvent) => {
    const newValue = event.currentTarget.value

    handleSetChevron('down')

    if (setFieldValue) {
      setFieldValue(name, newValue)
    } else {
      onChange(newValue)
    }
  }

  const handleClick = () => {
    if (onClick) onClick()
    handleSetChevron('up')
  }

  return (
    <InputWrapper>
      <InputContainer>
        {label && <LabelWrapper htmlFor={id}>{labelMessage}</LabelWrapper>}

        <InputSelectElement
          autoComplete={autoComplete}
          className={combinedClassName}
          data-field-error={hasError}
          data-id={dataId}
          disabled={disabled}
          isSelectEmpty={isEmpty(value)}
          hasEndIcon={Boolean(Icon)}
          id={id}
          isInvalid={isTouched && hasError}
          isPlaceholderAnimated={placeholder && isPlaceholderAnimated && !label}
          isIconPrepended={withIconPrepended}
          name={name}
          placeholder={isPlaceholderAnimated ? null : placeholderMessage}
          showValidIcon={showValidIcon}
          size={size}
          textTransformType={textTransformType}
          value={value}
          withBorder={withBorder}
          onBlur={handleBlur}
          onChange={handleChange}
          onClick={handleClick}
        >
          {children}
        </InputSelectElement>
        {isPlaceholderAnimated && !label && (
          <AnimatedPlaceholder>{placeholderMessage}</AnimatedPlaceholder>
        )}
        {Icon && withIconPrepended && (
          <IconsWrapperPrepended>
            <IconWrapperPrepended>{Icon}</IconWrapperPrepended>
          </IconsWrapperPrepended>
        )}
        <IconsWrapper>
          {showValidIcon && (
            <div>
              <ValidIcon size={ICON_SIZE} />
            </div>
          )}
          {Icon && !withIconPrepended && <IconWrapper>{Icon}</IconWrapper>}
          {withChevron && (
            <IconWrapper>
              <Chevron direction={chevronDirection} disabled={disabled} />
            </IconWrapper>
          )}
        </IconsWrapper>
      </InputContainer>
      {showErrorMessage && (
        <ValidationMessageElement>{errorMessage}</ValidationMessageElement>
      )}
    </InputWrapper>
  )
}

InputSelect.defaultProps = {
  hasError: false,
  isOptional: false,
  isPrivate: false,
  isRequired: false,
  isTouched: false,
  size: 'lg',
  textTransformType: 'none',
  withAnimatedPlaceholder: true,
  withBorder: true,
  withValidationIcon: true
}

export default InputSelect
